import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem as MuiMenuItem,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh,
  AutoGraph,
  CheckCircle,
  Close as CloseIcon,
  InfoOutlined,
  LinearScale as LinearScaleIcon,
  MoreVert as MoreVertIcon,
  PlayArrow,
  Preview,
  Refresh,
  Tune,
  ViewList as ViewListIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { featureFlags } from '../../config';
import type { MappingResponse, TransformationTestResponse, HealthIssue } from '../../services/api';
import { loadPreviewSamples } from '../../utils/preview';
import PreviewSampleTables from '../../components/PreviewSampleTables';
import DataExplorerTab from '../../components/workspace/DataExplorerTab';
import { materializeMappingViews } from '../../services/workspaceApi';
import { fetchReadiness } from '../../services/readinessApi';
import SideDrawer from '../../components/common/SideDrawer';
import {
  ReadinessChip,
  chipPropsFromFindings,
} from '../../components/workspace/readiness/ReadinessChip';
import { aggregateByRootCause } from '../../types/readiness';
import { useWorkspaceTables } from '../../hooks/useWorkspaceSession';
import type {
  ReadinessFinding,
  ReadinessStatus,
} from '../../types/readiness';

const ENABLE_DISTRIBUTED_PREVIEW = featureFlags.transformationPreview;

type MaterializeFailedView = { target?: string; view?: string; error?: string };

function formatMaterializeFailures(failedViews: MaterializeFailedView[]): string | null {
  if (!failedViews.length) return null;
  const first = failedViews[0];
  const table = first.target || first.view?.replace(/^__mapped_|__$/g, '') || 'mapped table';
  const detail = first.error || 'Unknown error';
  if (failedViews.length === 1) {
    return `Could not build preview for "${table}": ${detail}`;
  }
  return `Could not build preview for ${failedViews.length} tables. "${table}": ${detail}`;
}

function applyMaterializePreviewResult(result: {
  view_names: string[];
  failed_views?: MaterializeFailedView[];
}): { viewNames: string[]; error: string | null } {
  if (result.view_names.length > 0) {
    return { viewNames: result.view_names, error: null };
  }
  return {
    viewNames: [],
    error: formatMaterializeFailures(result.failed_views ?? []),
  };
}

type CanvasToolbarProps = {
  mapping?: MappingResponse;
  workbookId?: string;
  aggregationPlanLength?: number;
  manualAdjustmentCount?: number;
  onGenerate?: () => void;
  generating?: boolean;
  onApprove?: () => void;
  approving?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
  onToggleAggregationOverlay?: () => void;
  aggregationOverlayEnabled?: boolean;
  aggregationOverlayAvailable?: boolean;
  projectName?: string;
  schemaSetName?: string;
  schemaSetVersion?: number;
  guardrailWarning?: string | null;
  guardrailLabel?: string | null;
  editingBlocked?: boolean;
  onToggleGrid?: () => void;
  gridEnabled?: boolean;
  // Phase 3: Focus Mode
  focusModeEnabled?: boolean;
  onToggleFocusMode?: () => void;
  focusSelectionCount?: number;
  // Canvas View Mode
  canvasViewMode?: 'canvas' | 'mapping-table';
  onChangeViewMode?: (mode: 'canvas' | 'mapping-table') => void;
  // Recipe-Batch Decoupling: Generation mode + Load Data + execution badge
  generationMode?: 'auto' | 'full';
  onChangeGenerationMode?: (mode: 'auto' | 'full') => void;
  hasApprovedParent?: boolean;
  isApproved?: boolean;
  onLoadData?: () => void;
  loadingData?: boolean;
  executionCount?: number;
  /** Session ID for workspace-based preview (DataExplorerTab). */
  sessionId?: string;
  /** Batch ID for materializing mapping views. */
  batchId?: string;
  /** Imperative open preview for a specific target table (used from MappingGroupHeader icon). */
  previewTableRef?: React.MutableRefObject<((targetTable: string) => void) | null>;
  /** Batch row status — drives toolbar chip after mapping approval (transform / load / complete). */
  batchStatus?: string;
  /** Agent workspace `destination_status` — export lifecycle when embedded in workspace. */
  destinationStatus?: string;
  /** Health issues from the mapping health API — forwarded to DataExplorerTab for cell highlighting. */
  healthIssues?: HealthIssue[];
};

import { MappingDiagnostics } from '../../components/MappingDiagnostics';
import ActionableDisabledWrapper from '../../components/ActionableDisabledWrapper';
import { FocusModeToggle } from '../FocusOverlay';

/** Map recipe status + batch / export pipeline so the chip is not stuck on "Approved" after later steps. */
function deriveMappingLifecycleChip(
  recipeStatus: string | undefined,
  batchStatus: string | undefined,
  destinationStatus: string | undefined,
): { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' } {
  const ds = (destinationStatus || '').toLowerCase();
  if (ds === 'validating') return { label: 'Validating export', color: 'info' };
  if (ds === 'exporting') return { label: 'Exporting', color: 'info' };
  if (ds === 'exported') return { label: 'Exported', color: 'success' };
  if (ds === 'failed') return { label: 'Export failed', color: 'error' };

  const bs = batchStatus ?? '';
  if (bs === 'transforming') return { label: 'Transforming', color: 'info' };
  if (bs === 'loading') return { label: 'Loading', color: 'info' };
  if (bs === 'completed') return { label: 'Completed', color: 'success' };
  if (bs === 'failed') return { label: 'Failed', color: 'error' };
  if (bs === 'mapping') return { label: 'Mapping', color: 'info' };
  if (bs === 'ready_for_mapping') return { label: 'Ready for mapping', color: 'warning' };
  if (bs === 'mapped') return { label: 'Mapped', color: 'default' };
  if (bs === 'reviewing') return { label: 'In review', color: 'default' };

  const rs = recipeStatus ?? '';
  if (rs === 'approved') return { label: 'Approved', color: 'success' };
  if (rs === 'mapped') return { label: 'Mapped', color: 'default' };
  return { label: rs || '—', color: 'default' };
}

const CanvasToolbar: React.FC<CanvasToolbarProps> = ({
  mapping,
  workbookId,
  aggregationPlanLength,
  manualAdjustmentCount,
  onGenerate,
  generating,
  onApprove,
  approving,
  onRefresh,
  refreshing,
  onToggleAggregationOverlay,
  aggregationOverlayEnabled,
  aggregationOverlayAvailable = true,
  projectName,
  schemaSetName,
  schemaSetVersion,
  guardrailWarning,
  guardrailLabel,
  editingBlocked = false,
  onToggleGrid,
  gridEnabled,
  focusModeEnabled,
  onToggleFocusMode,
  focusSelectionCount,
  canvasViewMode = 'canvas',
  onChangeViewMode,
  // Recipe-Batch Decoupling
  generationMode,
  onChangeGenerationMode,
  hasApprovedParent,
  isApproved,
  onLoadData,
  loadingData,
  executionCount,
  sessionId,
  batchId,
  previewTableRef,
  batchStatus,
  destinationStatus,
  healthIssues,
}) => {
  if (!mapping) {
    return null;
  }

  const lifecycleChip = deriveMappingLifecycleChip(mapping.status, batchStatus, destinationStatus);

  // Transform API health issues to DataExplorerTab's HealthIssue shape
  const explorerHealthIssues = useMemo(() => {
    if (!healthIssues) return undefined;
    const filtered = healthIssues.filter(i => i.row_indices && i.row_indices.length > 0);
    if (filtered.length === 0) return undefined;
    return filtered.map(i => ({
      column: i.target_field,
      message: i.message,
      severity: i.severity as 'error' | 'warning' | 'info',
      remediation: i.remediation,
      rowIndices: i.row_indices,
    }));
  }, [healthIssues]);

  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewResponse, setPreviewResponse] = useState<TransformationTestResponse | null>(null);
  const [previewNotice, setPreviewNotice] = useState<string | null>(null);
  const [viewMenuAnchor, setViewMenuAnchor] = useState<null | HTMLElement>(null);

  // Workspace preview state (rich DataExplorerTab mode)
  const useWorkspacePreview = !!(sessionId && batchId);
  const queryClient = useQueryClient();
  const { data: workspaceTables = [] } = useWorkspaceTables(sessionId ?? null);

  const refreshWorkspaceTableMetadata = useCallback(async () => {
    if (!sessionId) return;
    await queryClient.invalidateQueries(['agent-workspace', sessionId]);
  }, [queryClient, sessionId]);

  // Readiness rollout — single fetch per mapping session, scoped to
  // the mapping_canvas consumer. Returns the union state across all
  // wired consumers (current backend implementation surfaces the
  // hierarchical-assembly findings; tabular consumers will populate
  // as their checks land). Findings drive: (1) the title-bar chip,
  // (2) the gating of the Approve button (disabled when not_ready).
  const [readinessFindings, setReadinessFindings] = React.useState<
    ReadinessFinding[]
  >([]);
  const [readinessStatus, setReadinessStatus] =
    React.useState<ReadinessStatus | null>(null);

  // Stable refresh fn used by initial fetch + stale-on-focus refetch.
  const refreshReadiness = React.useCallback(() => {
    if (!sessionId) return () => {};
    let cancelled = false;
    fetchReadiness({ sessionId, consumerKind: 'mapping_canvas' })
      .then((resp) => {
        if (cancelled) return;
        setReadinessFindings(resp.findings || []);
        setReadinessStatus(resp.status);
      })
      .catch(() => {
        if (cancelled) return;
        setReadinessFindings([]);
        setReadinessStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Initial fetch + re-fetch on recipe identity / known mapping-mutation
  // signals (recipe_id covers new-recipe, ai_confidence_score and
  // last-saved timestamps cover edits within the same recipe).
  React.useEffect(() => {
    return refreshReadiness();
  }, [
    refreshReadiness,
    mapping?.recipe_id,
    mapping?.ai_confidence_score,
    (mapping as any)?.updated_at,
    (mapping as any)?.last_modified,
  ]);

  // Plan §"Stale readiness" — when the user edits a mapping in another
  // panel and comes back, the chip should refresh on next focus
  // rather than stay stale until manual refresh. Listening on
  // ``visibilitychange`` + window ``focus`` captures both tab-return
  // and panel-blur-return cases without 422s or invalidation tokens.
  React.useEffect(() => {
    const onFocus = () => refreshReadiness();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshReadiness();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshReadiness]);

  // Aggregate raw findings by root cause (shared aggregateByRootCause
  // helper — same one ReviewPane banner + AssemblyInspectorPanel +
  // HierarchicalPreview use). Without this aggregation the chip
  // showed raw count (e.g. "8 to fix") while the banner showed the
  // collapsed count (e.g. "2 things to fix"), confusing the user
  // about which number is the "real" one.
  const readinessChipDerived = React.useMemo(
    () => chipPropsFromFindings(aggregateByRootCause(readinessFindings)),
    [readinessFindings],
  );
  const readinessNotReady = readinessStatus === 'not_ready';
  const [workspacePreviewOpen, setWorkspacePreviewOpen] = useState(false);
  const [workspaceViewNames, setWorkspaceViewNames] = useState<string[]>([]);
  const [workspacePreviewTable, setWorkspacePreviewTable] = useState<string | null>(null);
  const [materializeLoading, setMaterializeLoading] = useState(false);
  const [materializeError, setMaterializeError] = useState<string | null>(null);

  // Expose imperative "preview specific table" to parent via ref
  const handlePreviewForTable = useCallback(async (targetTable: string) => {
    if (!useWorkspacePreview || !mapping?.recipe_id) return;
    const viewName = `__mapped_${targetTable.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()}__`;
    setWorkspacePreviewOpen(true);
    setMaterializeLoading(true);
    setMaterializeError(null);
    try {
      const result = await materializeMappingViews(sessionId!, { batchId: batchId! });
      await refreshWorkspaceTableMetadata();
      const previewState = applyMaterializePreviewResult(result);
      setWorkspaceViewNames(previewState.viewNames);
      setMaterializeError(previewState.error);
      setWorkspacePreviewTable(
        previewState.viewNames.includes(viewName) ? viewName : previewState.viewNames[0] ?? null,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to materialize mapping views.';
      setMaterializeError(message);
    } finally {
      setMaterializeLoading(false);
    }
  }, [useWorkspacePreview, mapping?.recipe_id, sessionId, batchId, refreshWorkspaceTableMetadata]);

  // Assign to ref so parent can call it
  React.useEffect(() => {
    if (previewTableRef) {
      previewTableRef.current = handlePreviewForTable;
    }
    return () => {
      if (previewTableRef) previewTableRef.current = null;
    };
  }, [previewTableRef, handlePreviewForTable]);

  if (!mapping) {
    return null;
  }

  // R12: Extract diagnostics
  const skippedTables = mapping.transformation_plan?.skipped_tables || [];
  // rejected_relationships is in metadata, need to cast
  const rejectedRelationships = mapping.transformation_plan?.metadata?.rejected_relationships || [];
  const hasDiagnostics = skippedTables.length > 0 || rejectedRelationships.length > 0;

  const handlePreviewClick = async () => {
    if (!mapping?.recipe_id) {
      return;
    }
    // Use workspace-based preview when sessionId is available
    if (useWorkspacePreview) {
      setWorkspacePreviewOpen(true);
      setMaterializeLoading(true);
      setMaterializeError(null);
      try {
        const result = await materializeMappingViews(sessionId!, { batchId: batchId! });
        await refreshWorkspaceTableMetadata();
        const previewState = applyMaterializePreviewResult(result);
        setWorkspaceViewNames(previewState.viewNames);
        setMaterializeError(previewState.error);
        setWorkspacePreviewTable(previewState.viewNames[0] ?? null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to materialize mapping views.';
        setMaterializeError(message);
      } finally {
        setMaterializeLoading(false);
      }
      return;
    }
    // Fallback: old distributed preview
    setPreviewDialogOpen(true);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewNotice(null);
    setPreviewResponse(null);
    try {
      const result = await loadPreviewSamples(mapping.batch_id, mapping.recipe_id, 50);
      setPreviewResponse(result.response);
      setPreviewNotice(result.notice ?? null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load preview.';
      setPreviewError(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewLoading(false);
    setPreviewError(null);
    setPreviewResponse(null);
    setPreviewNotice(null);
  };

  const handleCloseWorkspacePreview = () => {
    setWorkspacePreviewOpen(false);
    setMaterializeError(null);
    setWorkspaceViewNames([]);
    setWorkspacePreviewTable(null);
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
          p: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          height: 64,
          flexShrink: 0,
          backgroundColor: 'background.paper',
          overflowX: 'auto',
          overflowY: 'hidden',
          flexWrap: 'nowrap',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexShrink={0}>
          <Typography variant="h6" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            Mapping Canvas
          </Typography>
          <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center' }} />
          {projectName && (
            <Chip
              size="small"
              label={projectName}
              sx={{ fontWeight: 500, backgroundColor: 'primary.50', color: 'primary.700' }}
            />
          )}
          <Chip
            size="small"
            label={lifecycleChip.label}
            variant="outlined"
            color={lifecycleChip.color}
          />
          {/* Readiness chip — single source of "is this ready?" truth.
              Replaces the misleading "Confidence 100%" badge that
              contradicted any data-quality issues. AI-mapping
              confidence lives on the chip tooltip. Renders in
              ``checking`` state while the initial /readiness fetch is
              in flight (plan §"What the user never sees" mandated the
              Confidence chip be removed entirely; no fallback). */}
          <ReadinessChip
            state={readinessStatus ? readinessChipDerived.state : 'checking'}
            count={readinessStatus ? readinessChipDerived.count : 0}
            size="small"
            tooltip={
              readinessStatus
                ? readinessNotReady
                  ? `${readinessChipDerived.count} thing${
                      readinessChipDerived.count === 1 ? '' : 's'
                    } to fix before approving. AI mapping confidence: ${Math.round(
                      (mapping.ai_confidence_score ?? 0) * 100,
                    )}%.`
                  : `AI mapping confidence: ${Math.round(
                      (mapping.ai_confidence_score ?? 0) * 100,
                    )}%.`
                : 'Checking readiness…'
            }
          />
          {guardrailWarning && (
             <Tooltip title={guardrailWarning}>
                <Chip size="small" label="Guardrail Active" color="warning" variant="outlined" />
             </Tooltip>
          )}
          
          {hasDiagnostics && (
            <Tooltip title="View Skipped Targets & Issues">
              <Button
                size="small"
                color="warning"
                variant="outlined"
                startIcon={<WarningAmberIcon />}
                onClick={() => setDiagnosticsOpen(true)}
              >
                Diagnostics
              </Button>
            </Tooltip>
          )}

          <Tooltip title="View Batch Details">
            <IconButton size="small" onClick={() => setInfoDialogOpen(true)}>
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={1} ml="auto" alignItems="center" flexShrink={0}>
          {/* Canvas View Mode Toggle */}
          {onChangeViewMode && (
            <ToggleButtonGroup
              value={canvasViewMode}
              exclusive
              onChange={(_e, val) => { if (val) onChangeViewMode(val as 'canvas' | 'mapping-table'); }}
              size="small"
              sx={{
                height: 32,
                '& .MuiToggleButton-root': {
                  px: 1,
                  py: 0.25,
                  fontSize: 12,
                  textTransform: 'none',
                },
              }}
            >
              <ToggleButton value="canvas">
                <Tooltip title="Canvas view with pipeline overview and field detail drill-down">
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LinearScaleIcon sx={{ fontSize: 16 }} />
                    <span>Canvas</span>
                  </Stack>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="mapping-table">
                <Tooltip title="Tabular mapping view with inline editing">
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <ViewListIcon sx={{ fontSize: 16 }} />
                    <span>Mapping</span>
                  </Stack>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Phase 3: Focus Mode Toggle */}
          {onToggleFocusMode && (
            <FocusModeToggle
                enabled={focusModeEnabled || false}
                onToggle={onToggleFocusMode}
                selectionCount={focusSelectionCount}
            />
          )}

          {/* View options overflow menu — groups secondary toggles */}
          <Tooltip title="View options">
            <IconButton size="small" onClick={(e) => setViewMenuAnchor(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={viewMenuAnchor}
            open={Boolean(viewMenuAnchor)}
            onClose={() => setViewMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {onToggleGrid && (
              <MuiMenuItem onClick={() => { onToggleGrid(); setViewMenuAnchor(null); }}>
                <ListItemIcon><Tune fontSize="small" color={gridEnabled ? 'primary' : 'inherit'} /></ListItemIcon>
                <ListItemText>{gridEnabled ? 'Hide Grid' : 'Show Grid'}</ListItemText>
              </MuiMenuItem>
            )}
            {onToggleAggregationOverlay && (
              <MuiMenuItem
                onClick={() => { onToggleAggregationOverlay(); setViewMenuAnchor(null); }}
                disabled={!aggregationOverlayAvailable}
              >
                <ListItemIcon><AutoGraph fontSize="small" color={aggregationOverlayEnabled ? 'secondary' : 'inherit'} /></ListItemIcon>
                <ListItemText>{aggregationOverlayEnabled ? 'Hide Aggregations' : 'Show Aggregations'}</ListItemText>
              </MuiMenuItem>
            )}
            {onRefresh && (
              <MuiMenuItem onClick={() => { onRefresh(); setViewMenuAnchor(null); }} disabled={refreshing}>
                <ListItemIcon><Refresh fontSize="small" /></ListItemIcon>
                <ListItemText>Refresh Graph</ListItemText>
              </MuiMenuItem>
            )}
          </Menu>

          {/* Generation Mode Toggle (visible when recipe has an approved parent) */}
          {hasApprovedParent && onChangeGenerationMode && (
            <ToggleButtonGroup
              value={generationMode ?? 'auto'}
              exclusive
              onChange={(_e, val) => { if (val) onChangeGenerationMode(val as 'auto' | 'full'); }}
              size="small"
              sx={{
                height: 32,
                '& .MuiToggleButton-root': {
                  px: 1,
                  py: 0.25,
                  fontSize: 12,
                  textTransform: 'none',
                },
              }}
            >
              <ToggleButton value="auto">
                <Tooltip title="Inherit approved mappings, only map new/changed fields">
                  <span>Delta</span>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="full">
                <Tooltip title="Start fresh — regenerate all mappings from scratch">
                  <span>Full</span>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Execution Count Badge */}
          {typeof executionCount === 'number' && executionCount > 0 && (
            <Chip
              size="small"
              label={`Applied ${executionCount}x`}
              icon={<PlayArrow fontSize="small" />}
              variant="outlined"
              color="success"
            />
          )}

          {ENABLE_DISTRIBUTED_PREVIEW && mapping.recipe_id && (
            <ActionableDisabledWrapper
               disabled={!!previewLoading}
               reason={"Preview is generating..."}
               disabledReasonTitle="Preview Loading"
            >
              <Button
                startIcon={<Preview />}
                variant="outlined"
                size="small"
                disabled={previewLoading}
                onClick={handlePreviewClick}
              >
                Preview
              </Button>
            </ActionableDisabledWrapper>
          )}

          {onApprove && (
            <ActionableDisabledWrapper
              disabled={editingBlocked || !!approving || readinessNotReady}
              reason={
                readinessNotReady
                  ? `Fix ${readinessChipDerived.count} issue${
                      readinessChipDerived.count === 1 ? '' : 's'
                    } in the Readiness panel before approving. Open the Readiness chip in the toolbar to see what to fix.`
                  : (guardrailWarning ?? 'Approval is processing...')
              }
              disabledReasonTitle={
                readinessNotReady ? 'Not ready' : 'Approval Disabled'
              }
            >
              <Button
                startIcon={<CheckCircle />}
                variant={readinessStatus === 'ready_with_notes' ? 'outlined' : 'contained'}
                size="small"
                disabled={editingBlocked || approving || readinessNotReady}
                onClick={() => {
                  // Plan §5 — when readiness is ``ready_with_notes`` the
                  // user is approving despite advisories. Confirm via
                  // a lightweight inline prompt before proceeding so
                  // the operator acknowledges the FYI items. The full
                  // listing modal is a follow-up; this is the minimum
                  // "you have N notes — proceed?" gate.
                  if (readinessStatus === 'ready_with_notes') {
                    const noteCount = readinessFindings.filter(
                      (f) => f.severity !== 'blocker',
                    ).length;
                    const msg = noteCount > 0
                      ? `${noteCount} note${noteCount === 1 ? '' : 's'} flagged for this mapping. Approve anyway?`
                      : 'Approve this mapping?';
                    if (!window.confirm(msg)) return;
                  }
                  onApprove();
                }}
                color="primary"
                style={{ fontWeight: 600 }}
              >
                Approve
              </Button>
            </ActionableDisabledWrapper>
          )}

        </Stack>
      </Stack>

      {hasDiagnostics && (
        <MappingDiagnostics
          open={diagnosticsOpen}
          onClose={() => setDiagnosticsOpen(false)}
          skippedTables={skippedTables}
          rejectedRelationships={rejectedRelationships}
        />
      )}

      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Batch Information
          <IconButton size="small" onClick={() => setInfoDialogOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <List dense>
            <ListItem>
              <ListItemText primary="Batch ID" secondary={mapping.batch_id} />
            </ListItem>
            {workbookId && (
              <ListItem>
                <ListItemText primary="Workbook ID" secondary={workbookId} />
              </ListItem>
            )}
            {schemaSetName && (
               <ListItem>
                 <ListItemText
                    primary="Schema Set"
                    secondary={`${schemaSetName} ${schemaSetVersion !== undefined ? `(v${schemaSetVersion})` : ''}`}
                 />
               </ListItem>
            )}
            <Divider component="li" />
            <ListItem>
               <ListItemText
                  primary="Aggregation Steps"
                  secondary={aggregationPlanLength ?? 0}
               />
            </ListItem>
            <ListItem>
               <ListItemText
                  primary="Manual Adjustments"
                  secondary={manualAdjustmentCount ?? 0}
               />
            </ListItem>
             {guardrailWarning && (
              <>
                <Divider component="li" />
                <ListItem>
                  <ListItemText
                    primary="Guardrail Warning"
                    secondary={guardrailWarning}
                    secondaryTypographyProps={{ color: 'error' }}
                  />
                </ListItem>
              </>
            )}
          </List>
        </DialogContent>
      </Dialog>

      <SideDrawer
        open={previewDialogOpen}
        onClose={handleClosePreview}
        title="Distributed Preview"
        defaultWidth="min(72vw, 1100px)"
      >
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {previewLoading && (
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Loading preview rows…</Typography>
            </Stack>
          )}

          {previewError && !previewLoading && <Alert severity="error">{previewError}</Alert>}

          {!previewLoading && !previewError && previewResponse && (
            <Stack spacing={2}>
              {previewNotice && <Alert severity="info">{previewNotice}</Alert>}
              {!!previewResponse.warnings?.length && (
                <Alert severity="warning">
                  <Box component="ul" sx={{ pl: 3, my: 0 }}>
                    {previewResponse.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </Box>
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                {previewResponse.sample_size} row sample · {previewResponse.dependent_sources.length} sources involved
              </Typography>
              <PreviewSampleTables sampleOutput={previewResponse.sample_output} />
            </Stack>
          )}
        </Box>
      </SideDrawer>

      {/* Workspace-based rich preview drawer (DataExplorerTab) */}
      <SideDrawer
        open={workspacePreviewOpen}
        onClose={handleCloseWorkspacePreview}
        title="Mapped Data Preview"
        defaultWidth="min(85vw, 1600px)"
      >
        {materializeLoading && (
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ py: 6, flex: 1 }}>
            <CircularProgress size={24} />
            <Typography variant="body2">Materializing mapping views…</Typography>
          </Stack>
        )}

        {materializeError && !materializeLoading && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{materializeError}</Alert>
          </Box>
        )}

        {!materializeLoading && !materializeError && workspaceViewNames.length > 0 && sessionId && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {workspaceViewNames.length > 1 && (
              <Tabs
                value={workspacePreviewTable ?? workspaceViewNames[0]}
                onChange={(_e, v) => setWorkspacePreviewTable(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: 36,
                  '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 12, textTransform: 'none' },
                }}
              >
                {workspaceViewNames.map((name) => (
                  <Tab key={name} value={name} label={name.replace(/^__mapped_|__$/g, '')} />
                ))}
              </Tabs>
            )}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <DataExplorerTab
                sessionId={sessionId}
                tableName={workspacePreviewTable}
                isSourceNode={false}
                workspaceTables={workspaceTables}
                healthIssues={explorerHealthIssues}
                targetSchemaSnapshot={mapping?.metadata?.target_schema_snapshot ?? (mapping as any)?.transformation_plan?.target_schema_snapshot}
              />
            </Box>
          </Box>
        )}

        {!materializeLoading && !materializeError && workspaceViewNames.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">No mapped tables found. Run mapping generation first.</Alert>
          </Box>
        )}
      </SideDrawer>
    </>
  );
};

export default React.memo(CanvasToolbar);
